import React, { Component, ErrorInfo, ReactNode, Suspense } from 'react';

interface Props {
  children: ReactNode;
  /** Name used in console diagnostics when the section fails. */
  name?: string;
  /** Optional placeholder rendered while the section is suspended. */
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
}

/**
 * Isolates a single page section.
 *
 * Why this exists: a single section that throws (or suspends forever) inside a
 * page-level <Suspense> prevents the ENTIRE page from committing — the user is
 * left staring at a loading skeleton with no error. Wrapping each independent
 * section means one bad section degrades to nothing while the rest of the page
 * renders normally.
 */
class SectionErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error(`[SectionBoundary${this.props.name ? `:${this.props.name}` : ''}]`, error, info);
  }

  render() {
    if (this.state.hasError) return null;
    return this.props.children;
  }
}

export const SectionBoundary = ({ children, name, fallback = null }: Props) => (
  <SectionErrorBoundary name={name}>
    <Suspense fallback={fallback}>{children}</Suspense>
  </SectionErrorBoundary>
);
